import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatMoney, tzOffset } from "@/lib/api";
import { useOutlets } from "@/lib/outlets";
import {
  TrendingUp, Receipt, Banknote, CreditCard, Smartphone, Wallet, Store, Users, Coffee, Calculator, PiggyBank, ArrowRight,
} from "lucide-react";

const RANGE_OPTIONS = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 365, label: "1 year" },
];

const m = (n) => `₹${formatMoney(n)}`;
const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

export default function Dashboard() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/dashboard/overview?days=${days}&tz_offset=${tzOffset()}`).then(({ data }) => setData(data));
  }, [days]);

  if (!data) return <div className="p-8 text-muted-foreground">Loading dashboard…</div>;

  return (
    <div className="readable h-full overflow-y-auto thin-scroll" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto p-6 lg:p-8 space-y-10">
        <div>
          <p className="text-base text-muted-foreground font-semibold">{greeting()} ·{" "}
            {new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mt-1">Today at Teaboy</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {data.outlet_count} outlet{data.outlet_count !== 1 && "s"} · {data.franchisee_count} franchisee{data.franchisee_count !== 1 && "s"} · live figures
          </p>
        </div>

        <TodayKpis t={data.today} />
        <OutletsToday outlets={data.outlets} />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3"><RecentOrders orders={data.recent_orders} /></div>
          <div className="lg:col-span-2"><ItemList testid="today-top-items" title="Selling today" items={data.today_top_items} empty="No sales yet today" /></div>
        </div>

        <section className="space-y-6 border-t border-border pt-10">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-extrabold">Performance</h2>
              <p className="text-sm text-muted-foreground mt-1">Last {days} days across all outlets</p>
            </div>
            <div className="flex items-center gap-1 border border-border bg-card p-1">
              {RANGE_OPTIONS.map((r) => (
                <button key={r.value} data-testid={`range-${r.value}`} onClick={() => setDays(r.value)}
                  className={`px-3.5 py-1.5 text-sm font-semibold transition-colors ${days === r.value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <RangeStrip t={data.totals} />
          <TrendChart trend={data.trend} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ItemList testid="top-items" title="Best sellers" items={data.top_items} empty="No sales in this range" />
            <Franchisees list={data.franchisees} />
          </div>
        </section>
        <div className="pb-12" />
      </div>
    </div>
  );
}

function TodayKpis({ t }) {
  const tiles = [
    { id: "orders", label: "Orders", value: t.orders, icon: <Receipt /> },
    { id: "avg", label: "Average bill", value: m(t.avg_ticket), icon: <Calculator /> },
    { id: "cash", label: "Cash", value: m(t.cash_total), icon: <Banknote /> },
    { id: "card", label: "Card", value: m(t.card_total), icon: <CreditCard /> },
    { id: "upi", label: "UPI", value: m(t.upi_total), icon: <Smartphone /> },
    { id: "expenses", label: "Expenses", value: m(t.expense_total), icon: <Wallet />, tone: "text-destructive" },
  ];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div className="bg-[hsl(var(--brand-plate))] text-primary-foreground p-6 flex flex-col justify-between animate-fade-up" data-testid="today-card">
        <div className="flex items-center gap-2 text-sm font-semibold opacity-75"><TrendingUp className="w-4 h-4" /> Today's sales</div>
        <div className="text-5xl font-extrabold mt-3 tabular-nums" data-testid="stat-revenue">{m(t.revenue)}</div>
        <div className="mt-6 pt-4 border-t border-white/15 flex items-center justify-between" data-testid="stat-net-cash">
          <span className="flex items-center gap-2 text-sm opacity-80"><PiggyBank className="w-4 h-4" /> Cash in hand</span>
          <span className={`text-2xl font-extrabold tabular-nums ${t.net_cash < 0 ? "text-red-400" : "text-primary"}`} title="Cash sales − expenses">{m(t.net_cash)}</span>
        </div>
      </div>
      <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-3 gap-4">
        {tiles.map((x, i) => (
          <div key={x.id} className="bg-card border border-border p-5 animate-fade-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`stat-${x.id}`}>
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground [&>svg]:w-4 [&>svg]:h-4">{x.icon} {x.label}</div>
            <div className={`text-3xl font-extrabold mt-2 tabular-nums ${x.tone || ""}`}>{x.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OutletsToday({ outlets }) {
  const { franchisees, setOutlet, outlets: all } = useOutlets();
  const nav = useNavigate();
  const open = (id) => {
    const o = all.find((x) => x.id === id);
    if (o) setOutlet(o);
    nav("/reports");
  };
  return (
    <section>
      <h2 className="text-2xl font-extrabold mb-4">Outlets today</h2>
      {outlets.length === 0 ? (
        <EmptyBox icon={<Store />} title="No outlets yet" hint="Add one from Admin → Outlets" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="outlets-perf">
          {outlets.map((o) => (
            <button key={o.id} onClick={() => open(o.id)} data-testid={`outlet-card-${o.id}`}
              className="group text-left bg-card border border-border p-5 hover:border-primary transition-colors">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-lg font-bold truncate">{o.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{franchisees.find((f) => f.id === o.franchisee_id)?.name || "—"}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-transform" />
              </div>
              <div className="flex items-baseline justify-between mt-4">
                <span className="text-3xl font-extrabold tabular-nums">{m(o.today.revenue)}</span>
                <span className="text-sm text-muted-foreground font-semibold">{o.today.orders} order{o.today.orders !== 1 && "s"}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-sm">
                <Mini label="Cash" value={m(o.today.cash_total)} />
                <Mini label="Card" value={m(o.today.card_total)} />
                <Mini label="UPI" value={m(o.today.upi_total)} />
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border text-sm">
                <span className="text-muted-foreground">Expenses <b className="text-destructive">{m(o.today.expense_total)}</b></span>
                <span className="text-muted-foreground">Cash in hand <b className="text-foreground">{m(o.today.net_cash)}</b></span>
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-background border border-border px-2.5 py-2">
      <div className="text-xs text-muted-foreground font-semibold">{label}</div>
      <div className="font-bold tabular-nums">{value}</div>
    </div>
  );
}

function RecentOrders({ orders }) {
  return (
    <section>
      <h2 className="text-xl font-bold mb-3">Recent orders</h2>
      {orders.length === 0 ? (
        <EmptyBox icon={<Receipt />} title="No orders yet" hint="Sales will show up here as they happen" />
      ) : (
        <div className="bg-card border border-border divide-y divide-border" data-testid="recent-orders">
          {orders.map((o) => (
            <div key={o.id} className="px-4 py-3 flex items-center gap-3" data-testid={`recent-order-${o.id}`}>
              <div className="w-20 shrink-0">
                <div className="text-sm font-bold">{new Date(o.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{o.outlet_name}</div>
                <div className="text-xs text-muted-foreground truncate">{o.items.map((it) => `${it.qty}× ${it.name}`).join(" · ")}</div>
              </div>
              <span className="text-xs font-bold uppercase bg-muted px-2 py-0.5 shrink-0">{o.payment_method}</span>
              <span className="font-extrabold tabular-nums w-20 text-right shrink-0">{m(o.total)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ItemList({ title, items, empty, testid }) {
  const max = Math.max(1, ...items.map((i) => i.revenue));
  return (
    <section>
      <h2 className="text-xl font-bold mb-3">{title}</h2>
      {items.length === 0 ? (
        <EmptyBox icon={<Coffee />} title={empty} hint="Data appears after your first order" />
      ) : (
        <div className="bg-card border border-border divide-y divide-border" data-testid={testid}>
          {items.map((it, i) => (
            <div key={it.name} className="px-4 py-3 flex items-center gap-3">
              <span className="w-5 text-sm font-bold text-muted-foreground">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{it.name}</div>
                <div className="h-1.5 bg-muted mt-1.5"><div className="h-full bg-primary/70" style={{ width: `${(it.revenue / max) * 100}%` }} /></div>
              </div>
              <span className="text-sm text-muted-foreground w-14 text-right">{it.qty} sold</span>
              <span className="font-bold tabular-nums w-20 text-right">{m(it.revenue)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function RangeStrip({ t }) {
  const cells = [
    ["Sales", m(t.revenue)], ["Orders", t.orders], ["Average bill", m(t.avg_ticket)],
    ["Cash", m(t.cash_total)], ["Card", m(t.card_total)], ["UPI", m(t.upi_total)],
    ["Expenses", m(t.expense_total)], ["Net", m(t.net_total)],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 bg-card border border-border divide-x divide-y lg:divide-y-0 divide-border" data-testid="range-totals">
      {cells.map(([l, v]) => (
        <div key={l} className="p-4">
          <div className="text-xs text-muted-foreground font-semibold">{l}</div>
          <div className="text-lg font-extrabold tabular-nums mt-1">{v}</div>
        </div>
      ))}
    </div>
  );
}

function TrendChart({ trend }) {
  const max = Math.max(1, ...trend.map((t) => t.revenue));
  const showLabels = trend.length <= 31;
  return (
    <div className="bg-card border border-border p-5" data-testid="trend-chart">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-lg font-bold">Daily sales</h3>
        <span className="text-xs text-muted-foreground font-semibold">Peak {m(max === 1 ? 0 : max)}</span>
      </div>
      <div className="h-44 flex items-end gap-1">
        {trend.map((t) => (
          <div key={t.date} title={`${t.date} · ${m(t.revenue)}`} className="flex-1 min-w-0 h-full flex flex-col justify-end items-center gap-1 group">
            {trend.length <= 7 && t.revenue > 0 && <span className="text-xs font-bold tabular-nums">{m(t.revenue)}</span>}
            <div className={`w-full ${t.revenue > 0 ? "bg-primary" : "bg-muted"} group-hover:opacity-75 transition-opacity`}
              style={{ height: `${t.revenue > 0 ? Math.max(4, (t.revenue / max) * 85) : 2}%` }} />
          </div>
        ))}
      </div>
      {showLabels && (
        <div className="flex gap-1 mt-2">
          {trend.map((t, i) => (
            <div key={t.date} className="flex-1 min-w-0 text-center text-[11px] text-muted-foreground font-semibold">
              {trend.length <= 7 || i % 5 === 0 ? new Date(`${t.date}T00:00:00`).toLocaleDateString(undefined, trend.length <= 7 ? { weekday: "short" } : { day: "numeric", month: "short" }) : ""}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Franchisees({ list }) {
  return (
    <section>
      <h2 className="text-xl font-bold mb-3">Franchisees</h2>
      {list.length === 0 ? (
        <EmptyBox icon={<Users />} title="No franchisees yet" hint="Add one from Admin → Franchisees" />
      ) : (
        <div className="bg-card border border-border divide-y divide-border" data-testid="franchisees-perf">
          {list.map((f) => (
            <div key={f.id} className="px-4 py-3 flex items-center justify-between" data-testid={`franchisee-card-${f.id}`}>
              <div>
                <div className="font-semibold">{f.name}</div>
                <div className="text-xs text-muted-foreground">{f.outlet_count} outlet{f.outlet_count !== 1 && "s"} · {f.range.orders} orders</div>
              </div>
              <div className="text-lg font-extrabold tabular-nums">{m(f.range.revenue)}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function EmptyBox({ icon, title, hint }) {
  return (
    <div className="border border-dashed border-border p-8 text-center flex flex-col items-center gap-2 text-muted-foreground">
      <span className="[&>svg]:w-8 [&>svg]:h-8">{icon}</span>
      <div className="text-base font-semibold text-foreground">{title}</div>
      <div className="text-sm">{hint}</div>
    </div>
  );
}
